export const roles = {
  admin: "admin",
  tardeomanager: "tardeo_manager",
  goregaonmanager: "goregaon_manager",
  contentmanager: "content_manager",
  inventorymanager: "inventory_manager",
  navimumbaimanager: "navimumbai_manager",
  thanemanager: "thane_manager",
  othersmanager: "others_manager",
};

// Helper function to create a warehouse manager role key
export const createWarehouseManagerRoleKey = (warehouseName) => {
  // Convert warehouse name to lowercase and replace spaces with underscores
  const normalizedName = warehouseName.toLowerCase().replace(/\s+/g, '_');
  return `${normalizedName}_manager`;
};

// Get all roles including dynamically created ones from localStorage
export const getAllRoles = () => {
  // Start with our static roles
  const allRoles = {...roles};
  
  // Try to get dynamically created roles from localStorage
  try {
    // 1. Get available warehouses and create manager roles for each
    const availableWarehouses = JSON.parse(localStorage.getItem("availableWarehouses") || "[]");
    if (Array.isArray(availableWarehouses)) {
      availableWarehouses.forEach(warehouse => {
        const roleKey = warehouse.toLowerCase().replace(/\s+/g, '_') + '_manager';
        const roleDisplayName = createWarehouseManagerRoleKey(warehouse);
        
        // Only add if it's not already in the static roles
        if (!Object.values(allRoles).includes(roleDisplayName)) {
          allRoles[warehouse.toLowerCase().replace(/\s+/g, '') + 'manager'] = roleDisplayName;
        }
      });
    }
    
    // 2. Get explicitly stored dynamic roles
    const dynamicRoles = JSON.parse(localStorage.getItem("dynamicRoles") || "{}");
    
    // 3. Check auth to make sure we don't miss any roles
    const auth = JSON.parse(localStorage.getItem("auth")) || {};
    const authRoles = auth.roles || [];
    
    // Extract any warehouse manager roles from auth that might not be captured yet
    if (Array.isArray(authRoles)) {
      authRoles.forEach(role => {
        if (typeof role === 'string' && role.endsWith('_manager')) {
          // Get warehouse name from role
          const warehouseName = role.replace('_manager', '');
          
          // Create a key for this role
          const keyName = warehouseName + 'manager';
          
          // Only add if it doesn't already exist
          if (!allRoles[keyName] && !Object.values(allRoles).includes(role)) {
            allRoles[keyName] = role;
            
            // Also add to dynamic roles for persistence
            dynamicRoles[keyName] = role;
          }
        }
      });
    }
    
    // Update dynamic roles in localStorage
    localStorage.setItem("dynamicRoles", JSON.stringify(dynamicRoles));
    
    // Return combined roles
    return {...allRoles, ...dynamicRoles};
  } catch (error) {
    console.error("Error loading dynamic roles:", error);
    return allRoles;
  }
};

// Check if a role is a warehouse manager role (static or dynamic)
export const isWarehouseManagerRole = (role) => {
  if (!role || typeof role !== 'string') {
    return false;
  }
  
  // Check if it ends with _manager
  return role.endsWith('_manager');
};

// Get warehouse name from manager role
export const getWarehouseFromRole = (role) => {
  if (!isWarehouseManagerRole(role)) {
    return null;
  }
  
  // Remove _manager suffix to get warehouse name
  const warehouseName = role.replace('_manager', '');
  
  // Capitalize first letter for display
  // Handle specific case for "NaviMumbai"
  // Make the check case-insensitive to handle potential variations like 'Navimumbai'
  if (warehouseName.toLowerCase() === 'navimumbai') {
    return 'NaviMumbai'; // Ensure correct casing is always returned
  }
  // Default: Capitalize first letter
  return warehouseName.charAt(0).toUpperCase() + warehouseName.slice(1);
};

// Add a new role for a warehouse
export const addWarehouseManagerRole = (warehouseName) => {
  if (!warehouseName) {
    return null;
  }
  
  try {
    // Create the role name
    const roleName = createWarehouseManagerRoleKey(warehouseName);
    
    // Create a key for storing in our object
    const roleKey = warehouseName.toLowerCase().replace(/\s+/g, '') + 'manager';
    
    // Get existing dynamic roles
    const dynamicRoles = JSON.parse(localStorage.getItem("dynamicRoles") || "{}");
    
    // Add the new role
    dynamicRoles[roleKey] = roleName;
    
    // Save back to localStorage
    localStorage.setItem("dynamicRoles", JSON.stringify(dynamicRoles));
    
    console.log(`Added new warehouse manager role: ${roleName}`);
    
    // Force a refresh of the available warehouses to ensure consistency
    if (window.forceWarehouseRefresh) {
      window.forceWarehouseRefresh();
    }
    
    return roleName;
  } catch (error) {
    console.error("Error adding warehouse manager role:", error);
    return null;
  }
};

// Ensure a warehouse role exists - create it if it doesn't
export const ensureWarehouseRole = (warehouseName) => {
  // Get all current roles
  const allRoles = getAllRoles();
  
  // Create the expected role name
  const expectedRoleName = createWarehouseManagerRoleKey(warehouseName);
  
  // Check if it exists already
  if (Object.values(allRoles).includes(expectedRoleName)) {
    console.log(`Warehouse role ${expectedRoleName} already exists`);
    return expectedRoleName;
  }
  
  // If not, create it
  return addWarehouseManagerRole(warehouseName);
};

// Initialize on load - ensure all warehouse roles exist
(() => {
  try {
    // Get available warehouses
    const availableWarehouses = JSON.parse(localStorage.getItem("availableWarehouses") || "[]");
    
    // Create roles for each warehouse
    if (Array.isArray(availableWarehouses)) {
      availableWarehouses.forEach(warehouse => {
        ensureWarehouseRole(warehouse);
      });
    }
  } catch (error) {
    console.error("Error initializing warehouse roles:", error);
  }
})();